import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from "react";
import { SCENARIOS, SYSTEM_INFO, ZONE_BY_ID } from "../data/city";
import { canDecide, evaluateRun } from "../simulation/engine";
import {
  DURATION,
  STEP,
  type Decision,
  type Parameters,
  type Run,
  type ScenarioId,
  type System,
  type ZoneId,
} from "../simulation/types";
import { loadSession, saveSession } from "../lib/persistence";
export type Mode = "explore" | "simulate" | "respond";
export type Layer = "activity" | "water" | "transport" | "power";
export interface State {
  run: Run;
  comparison: Run | null;
  mode: Mode;
  perspective: "operator" | "resident";
  layer: Layer;
  selected: ZoneId | null;
  playing: boolean;
  speed: 1 | 2 | 4;
  ready: boolean;
}
type Action =
  | { type: "scenario"; id: ScenarioId }
  | { type: "parameter"; key: keyof Parameters; value: number }
  | { type: "time"; minute: number }
  | { type: "play" }
  | { type: "speed" }
  | { type: "mode"; mode: Mode }
  | { type: "perspective"; perspective: State["perspective"] }
  | { type: "layer"; layer: Layer }
  | { type: "select"; zone: ZoneId | null }
  | { type: "decision"; decision: Decision }
  | { type: "undo" }
  | { type: "reset" }
  | { type: "compare" }
  | { type: "restore"; run: Run; comparison: Run | null }
  | { type: "loaded" };
export const initialState: State = {
  run: {
    parameters: { ...SCENARIOS.calm.parameters },
    decisions: [],
    scenario: "calm",
    minute: 0,
  },
  comparison: null,
  mode: "explore",
  perspective: "operator",
  layer: "activity",
  selected: null,
  playing: false,
  speed: 1,
  ready: false,
};
export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "scenario":
      return {
        ...state,
        run: {
          parameters: { ...SCENARIOS[action.id].parameters },
          decisions: [],
          scenario: action.id,
          minute: 0,
        },
        playing: false,
        selected: null,
        layer:
          action.id === "storm"
            ? "water"
            : action.id === "rail"
              ? "transport"
              : action.id === "energy"
                ? "power"
                : "activity",
      };
    case "parameter":
      return {
        ...state,
        run: {
          ...state.run,
          parameters: { ...state.run.parameters, [action.key]: action.value },
        },
        playing: false,
      };
    case "time":
      return {
        ...state,
        run: {
          ...state.run,
          minute: Math.max(
            0,
            Math.min(DURATION, Math.round(action.minute / STEP) * STEP),
          ),
        },
        playing: action.minute >= DURATION ? false : state.playing,
      };
    case "play":
      return {
        ...state,
        playing: !state.playing,
        run: {
          ...state.run,
          minute: state.run.minute === DURATION ? 0 : state.run.minute,
        },
      };
    case "speed":
      return {
        ...state,
        speed: state.speed === 1 ? 2 : state.speed === 2 ? 4 : 1,
      };
    case "mode":
      return { ...state, mode: action.mode };
    case "perspective":
      return { ...state, perspective: action.perspective };
    case "layer":
      return { ...state, layer: action.layer };
    case "select":
      return { ...state, selected: action.zone };
    case "decision":
      return canDecide(
        state.run.decisions,
        action.decision.minute,
        action.decision.zone,
        action.decision.system,
        action.decision.delta,
      )
        ? {
            ...state,
            run: {
              ...state.run,
              decisions: [...state.run.decisions, action.decision],
            },
          }
        : state;
    case "undo":
      return {
        ...state,
        run: { ...state.run, decisions: state.run.decisions.slice(0, -1) },
      };
    case "reset":
      return {
        ...state,
        run: { ...state.run, decisions: [], minute: 0 },
        playing: false,
      };
    case "compare":
      return { ...state, comparison: structuredClone(state.run) };
    case "restore":
      return {
        ...state,
        run: action.run,
        comparison: action.comparison,
        playing: false,
        ready: true,
      };
    case "loaded":
      return { ...state, ready: true };
  }
}
function useCityState() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [saveStatus, setSaveStatus] = useState<
    "loading" | "saved" | "memory" | "saving"
  >("loading");
  const [restored, setRestored] = useState(false);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 7000);
    return () => clearTimeout(timer);
  }, [notice]);
  useEffect(() => {
    let alive = true;
    loadSession().then((s) => {
      if (alive) {
        if (s) {
          dispatch({ type: "restore", run: s.run, comparison: s.comparison });
          setRestored(true);
        } else dispatch({ type: "loaded" });
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    if (!state.ready) return;
    setSaveStatus("saving");
    const timer = setTimeout(
      () =>
        saveSession({
          version: 1,
          run: state.run,
          comparison: state.comparison,
          savedAt: new Date().toISOString(),
        }).then(setSaveStatus),
      350,
    );
    return () => clearTimeout(timer);
  }, [state.run, state.comparison, state.ready]);
  useEffect(() => {
    if (!state.playing) return;
    const id = setInterval(
      () => dispatch({ type: "time", minute: state.run.minute + STEP }),
      1400 / state.speed,
    );
    return () => clearInterval(id);
  }, [state.playing, state.run.minute, state.speed]);
  const result = useMemo(() => evaluateRun(state.run), [state.run]);
  const comparison = useMemo(
    () => (state.comparison ? evaluateRun(state.comparison) : null),
    [state.comparison],
  );
  const decide = (zone: ZoneId, system: System, delta: 1 | -1) => {
    if (
      state.run.minute === DURATION ||
      !canDecide(state.run.decisions, state.run.minute, zone, system, delta)
    )
      return;
    dispatch({
      type: "decision",
      decision: {
        id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        minute: state.run.minute,
        zone,
        system,
        delta,
      },
    });
    setNotice(
      `${SYSTEM_INFO[system].crew} ${delta === 1 ? "sent to" : "withdrawn from"} ${ZONE_BY_ID[zone].short}.${delta === 1 ? " Arrives in 5 simulated minutes." : ""}`,
    );
  };
  return {
    state,
    dispatch,
    ...result,
    comparison,
    decide,
    saveStatus,
    restored,
    notice,
    clearNotice: () => setNotice(""),
  };
}
type CityContextValue = ReturnType<typeof useCityState>;
const Context = createContext<CityContextValue | null>(null);
export function CityProvider({ children }: { children: ReactNode }) {
  const city = useCityState();
  return <Context.Provider value={city}>{children}</Context.Provider>;
}
export function useCity() {
  const c = useContext(Context);
  if (!c) throw new Error("CityProvider missing");
  return c;
}
